import { FormGroup, FormControl, Validators } from '@angular/forms'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { NgxPermissionsService } from 'ngx-permissions'
import { Map } from 'immutable'
import {
  getPropertyState,
  getCreatePropertyState,
} from './../properties.selectors'
import {
  SetDefaultData,
  FetchPropertyRequest,
  UpdatePropertyRequest,
  UpdateField,
  CreatePropertyRequest,
  SetPropertyFieldVisitRequest,
  ArchivePropertyRequest,
} from './../properties.actions'
import { Store, select } from '@ngrx/store'
import { Subscription, Subject } from 'rxjs'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router'
import Validator from '@shared/validatorjs/src/validator'
import FormTrait from '@traits/form.trait'
import { isContentEditableEnabled } from '@containers/i18n/store/i18n.selector'

@Component({
  selector: 'app-property',
  templateUrl: './property.component.html',
  styleUrls: ['./property.component.css'],
})
export class PropertyComponent extends FormTrait implements OnInit, OnDestroy {
  public propertyId: number
  public nextStatus: string
  public prevStatus: string
  private state$: Subscription
  public isLoading: boolean
  public isSaving: boolean
  private parent: string
  public tab: string
  public status: string
  public isModalOpen: boolean
  public isFieldVisitModalOpen: boolean
  public isForward: boolean = true
  public statuses: Array<string> = [
    'DATA_ENTRY',
    'ASSESSMENT',
    'BILLING',
    'OBJECTION',
  ]
  public showAppealNote: boolean
  public showAppealReason: boolean
  public showAppealResolution: boolean
  public showReturnReason: boolean
  public sendTo: string
  public routerEvents$: Subscription
  public hasReadonlyPermission: boolean = false
  private settings$: Subscription
  private minLat: number
  private maxLat: number
  private minLng: number
  private maxLng: number
  public fieldVisit: boolean
  public fieldVisitForm: FormGroup
  public appealId: number
  public fieldVisitCommentError: string
  public isTranslationActive = false
  public isTranslationActive$: Subscription
  public propertiesTabToNavigate = ''
  public isReadonly: boolean

  constructor(
    public permissionsService: NgxPermissionsService,
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    super()
  }

  ngOnInit() {
    this.isTranslationActive$ = this.store
      .pipe(select(isContentEditableEnabled))
      .subscribe((isTranslationActive) => {
        this.isTranslationActive = isTranslationActive
      })
    this.store.dispatch<any>(new SetDefaultData('property'))
    this.propertyId = parseInt(this.route.snapshot.params.id, 10)
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined
    const urlSegments = this.router.url.split('/')
    this.tab = urlSegments[urlSegments.length - 1]
    this.parent = this.propertyId ? 'property' : 'createProperty'
    this.store.dispatch<any>(new FetchPropertyRequest(this.propertyId))
    this.fieldVisitForm = new FormGroup({
      fieldVisit: new FormControl(false),
      fieldVisitComment: new FormControl(''),
    })

    this.fieldVisitForm.get('fieldVisit').valueChanges.subscribe((checked) => {
      const commentControl = this.fieldVisitForm.get('fieldVisitComment')
      if (checked) {
        commentControl.setValidators([Validators.required])
      } else {
        commentControl.clearValidators()
      }
      commentControl.updateValueAndValidity()
    })
    const getState = this.propertyId ? getPropertyState : getCreatePropertyState
    this.state$ = this.store.pipe(select(getState)).subscribe((state) => {
      this.state = state
      this.isLoading = state.get('isSaving')

      if (this.propertyId) {
        this.status = this.state.getIn(['data', 'status', 'name']) as string
        this.isReadonly =
          this.state.getIn(['data', 'status', 'name']) === 'ARCHIVED' ||
          this.hasReadonlyPermission

        if (
          this.status?.toLocaleLowerCase() === 'objection' ||
          this.status?.toLocaleLowerCase() === 'billing' ||
          this.status?.toLocaleLowerCase() === 'assessment' ||
          this.status?.toLocaleLowerCase() === 'data_entry'
        ) {
          this.propertiesTabToNavigate = this.status?.toLocaleLowerCase()
        } else {
          this.propertiesTabToNavigate = ''
        }

        const currentStatusIndex = this.statuses.indexOf(this.status)
        this.appealId = this.state.getIn(['data', 'appeal', 'id']) as number
        this.fieldVisitForm
          .get('fieldVisit')
          .setValue(this.state.getIn(['data', 'appeal', 'fieldVisit']))
        this.fieldVisitForm
          .get('fieldVisitComment')
          .setValue(this.state.getIn(['data', 'appeal', 'fieldVisitComment']))

        if (
          currentStatusIndex !== -1 &&
          this.statuses[currentStatusIndex + 1]
        ) {
          this.nextStatus = this.statuses[currentStatusIndex + 1]
        }

        if (
          currentStatusIndex !== -1 &&
          this.statuses[currentStatusIndex - 1]
        ) {
          if (this.status === 'OBJECTION') {
            this.prevStatus = this.statuses[0]
          } else {
            this.prevStatus = this.statuses[currentStatusIndex - 1]
          }
        }
      }
    })
    this.routerEvents$ = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        const urlSegments = e.url.split('/')
        this.tab = urlSegments[urlSegments.length - 1]
      }
    })

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        const settings = state.get('data')
        const gisExtent = settings.get('gisExtent')

        if (gisExtent) {
          const coordinates = gisExtent
            .split(',')
            .map((c: string) => parseFloat(c))

          this.minLat = coordinates[1]
          this.maxLat = coordinates[3]
          this.minLng = coordinates[0]
          this.maxLng = coordinates[2]
        }
      })
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.routerEvents$.unsubscribe()
    this.settings$.unsubscribe()

    this.store.dispatch<any>(new SetDefaultData('property'))
    this.isTranslationActive$.unsubscribe()
  }

  public toggleFieldVisitModal() {
    this.isFieldVisitModalOpen = !this.isFieldVisitModalOpen
  }

  public saveFieldVisit() {
    if (this.fieldVisitForm.invalid) {
      this.fieldVisitForm.markAllAsTouched()
      return
    }

    this.isFieldVisitModalOpen = false
    this.store.dispatch(
      new SetPropertyFieldVisitRequest(
        this.appealId,
        this.fieldVisitForm.value,
      ),
    )
    this.fieldVisitForm.reset()
  }

  public toggleModal(sendTo: string) {
    if (this.isTranslationActive) return

    if (this.isModalOpen) {
      this.sendTo = undefined
    } else {
      this.sendTo = sendTo
    }
    this.isModalOpen = !this.isModalOpen

    switch (sendTo) {
      case 'OBJECTION':
        if (this.status === 'ASSESSMENT' && this.getField(['appeal'])) {
          this.showAppealNote = true
        }
        if (this.status === 'BILLING' && !this.getField(['appeal'])) {
          this.showAppealReason = true
        }
        break

      case 'ASSESSMENT':
        if (this.status === 'DATA_ENTRY' && this.getField(['appeal'])) {
          this.showAppealNote = true
        }
        if (this.status === 'OBJECTION' && this.getField(['appeal'])) {
          this.showAppealNote = true
        }
        break

      case 'BILLING':
        if (this.status === 'ASSESSMENT' && this.getField(['appeal'])) {
          this.showAppealResolution = true
        }
        break

      case 'DATA_ENTRY':
        if (this.status === 'ASSESSMENT' && !this.getField(['appeal'])) {
          this.showReturnReason = true
        }
        break

      default:
        break
    }
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name
          ? e
          : e.target.type === 'checkbox'
          ? e.target.checked
          : e.target.value,
        `${this.parent}.data`,
      ),
    )

    if (e.target.name === 'fieldVisit') {
      this.fieldVisit = e.target.checked

      if (!e.target.checked) {
        this.store.dispatch(
          new UpdateField('fieldVisitComment', '', `${this.parent}.data`),
        )
      }
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        this.parent,
      ),
    )
  }

  archive() {
    this.store.dispatch(new ArchivePropertyRequest(this.propertyId))
  }

  validation(saveAndSend?: boolean) {
    const data = this.state.get('data')
    const commonRules: any = {}
    const rules = {
      details: {
        // numberOfStories: 'numeric|required|min:0'
      },
      contacts: {
        'contacts.*.business.businessProportion': 'numeric|min:0',
      },
      measure: {},
      gallery: {},
      features: {},
      documents: {},
    }

    if (this.showAppealNote) {
      commonRules.appealNote = 'required'
    }

    if (this.showAppealReason) {
      commonRules.appealReason = 'required'
    }

    if (this.showAppealResolution) {
      commonRules.appealResolution = 'required'
    }

    if (this.status !== 'DATA_ENTRY' && saveAndSend) {
      rules.measure = {
        'building.area': 'numeric|required',
        'building.pointLat': `required|numeric`,
        'building.pointLng': `required|numeric`,
      }
    }

    const validator = new Validator(
      data.toJS(),
      { ...commonRules, ...rules[this.tab] },
      {
        required: 'The field is required.',
        required_with: 'The field is required.',
        min: 'The field must be at least :min.',
      },
    )

    validator.setAttributeNames({
      area: 'roof top area',
      pointLat: 'coordinate lat',
      pointLng: 'coordinate lng',
      appealReason: 'appeal reason',
      appealNote: 'appeal note',
      appealResolution: 'appeal resolution',
      numberOfStories: 'number of stories',
      businessProportion: 'business proportion',
      assessibility: 'assessable',
    })

    return validator
  }

  public save(saveAndSend = false): void {
    if (this.isTranslationActive) return

    const validation = this.validation(saveAndSend)
    const payload = this.state.get('data').toJS()
    const status = this.sendTo ? this.sendTo : this.nextStatus
    const createOrUpdate = this.propertyId
      ? new UpdatePropertyRequest(
          this.propertyId,
          {
            ...payload,
            building: {
              ...payload.building,
              buildingLayerFeatures: this.state
                .get('buildingLayerFeatures')
                .toJS(),
              // buildingLayerFeatures: this.state.get('buildingLayerFeatures')
              //   .size
              //   ? this.state.get('buildingLayerFeatures').toJS()
              //   : (
              //       this.state.getIn([
              //         'data',
              //         'building',
              //         'buildingLayerFeatures',
              //       ]) as any
              //     )
              //       .toJS()
              //       .map(({ layerFeature }) => layerFeature),
            },
          },
          saveAndSend ? status : null,
        )
      : new CreatePropertyRequest({
          ...payload,
          building: {
            ...payload.building,
            buildingLayerFeatures: this.state
              .get('buildingLayerFeatures')
              .toJS(),
          },
        })

    if (validation.passes()) {
      if (this.isModalOpen) this.isModalOpen = false
      this.store.dispatch(createOrUpdate)
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          this.parent,
        ),
      )
    }
  }

  public tabChanged(tab: Array<string>) {
    const validation = this.validation()

    if (validation.passes()) {
      this.router.navigate(['properties', this.propertyId, ...tab])
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          this.parent,
        ),
      )
    }
  }
}
